/**
 * PortalNotifications - Bell icon with dropdown notification list
 * Deployed to: frontend/src/pages/portal/PortalNotifications.js
 */
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, MessageSquare, FileText, DollarSign, Calendar, CheckCircle, X } from 'lucide-react';
import clientPortalService from '../../services/clientPortalService';

const typeIcons = {
  message: MessageSquare,
  document: FileText,
  invoice: DollarSign,
  payment: DollarSign,
  appointment: Calendar,
  onboarding: CheckCircle,
  system: Bell,
};

const typeColors = {
  message: 'text-cyan-400 bg-cyan-500/20',
  document: 'text-blue-400 bg-blue-500/20',
  invoice: 'text-amber-400 bg-amber-500/20',
  payment: 'text-green-400 bg-green-500/20',
  appointment: 'text-purple-400 bg-purple-500/20',
  onboarding: 'text-emerald-400 bg-emerald-500/20',
  system: 'text-gray-400 bg-gray-500/20',
};

export default function PortalNotifications({ unreadCount = 0, onCountChange }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef(null);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Bootstrap the badge on mount. The WebSocket 'connected' push is the other
  // source -- the bell spent its whole life badge-less because that was the
  // ONLY one and the WS was returning 403; the badge must not have a single
  // point of failure again.
  useEffect(() => {
    loadNotifications();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadNotifications = async () => {
    try {
      setLoading(true);
      const data = await clientPortalService.getNotifications(false, 20);
      setNotifications(data.notifications || []);
      if (onCountChange) onCountChange(data.unread_count || 0);
    } catch (err) {
      console.error('Failed to load notifications:', err);
    } finally {
      setLoading(false);
    }
  };

  const toggleOpen = () => {
    if (!open) loadNotifications();
    setOpen(!open);
  };

  const markRead = async (id) => {
    try {
      await clientPortalService.markNotificationRead(id);
      setNotifications(prev =>
        prev.map(n => n.id === id ? { ...n, is_read: true } : n)
      );
      if (onCountChange) onCountChange(Math.max(0, unreadCount - 1));
    } catch (err) {
      console.error('Failed to mark read:', err);
    }
  };

  const markAllRead = async () => {
    try {
      await clientPortalService.markAllNotificationsRead();
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      if (onCountChange) onCountChange(0);
    } catch (err) {
      console.error('Failed to mark all read:', err);
    }
  };

  const handleClick = (notif) => {
    if (!notif.is_read) markRead(notif.id);
    setOpen(false);

    // Navigate based on type
    const data = notif.data || {};
    switch (notif.notification_type) {
      case 'message':
        navigate('/portal/messages');
        break;
      case 'document':
        navigate('/portal/documents');
        break;
      case 'invoice':
      case 'payment':
        navigate('/portal/invoices');
        break;
      case 'appointment':
        navigate('/portal/appointments');
        break;
      case 'onboarding':
        navigate('/portal/onboarding');
        break;
      default:
        navigate('/portal/dashboard');
    }
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    const now = new Date();
    const diffMs = now - d;
    const diffMins = Math.floor(diffMs / 60000);
    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHrs = Math.floor(diffMins / 60);
    if (diffHrs < 24) return `${diffHrs}h ago`;
    const diffDays = Math.floor(diffHrs / 24);
    if (diffDays < 7) return `${diffDays}d ago`;
    return d.toLocaleDateString();
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={toggleOpen}
        className="relative text-virgo-teal-400 hover:text-white transition-colors p-1"
        title="Notifications"
      >
        <Bell size={20} />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs w-5 h-5 flex items-center justify-center rounded-full font-bold">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-2 w-80 bg-virgo-navy-700 border border-virgo-teal-700/50 rounded-xl shadow-2xl z-50 overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-virgo-teal-700/30">
            <h3 className="text-white font-semibold text-sm">Notifications</h3>
            <div className="flex items-center gap-2">
              {unreadCount > 0 && (
                <button
                  onClick={markAllRead}
                  className="text-xs text-cyan-400 hover:text-cyan-300"
                >
                  Mark all read
                </button>
              )}
              <button onClick={() => setOpen(false)} className="text-virgo-teal-500 hover:text-white">
                <X size={16} />
              </button>
            </div>
          </div>

          {/* List */}
          <div className="max-h-96 overflow-y-auto">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-cyan-500" />
              </div>
            ) : notifications.length === 0 ? (
              <div className="text-center py-8 text-virgo-teal-500 text-sm">
                No notifications yet
              </div>
            ) : (
              notifications.map(notif => {
                const Icon = typeIcons[notif.notification_type] || Bell;
                const colorClass = typeColors[notif.notification_type] || typeColors.system;
                return (
                  <button
                    key={notif.id}
                    onClick={() => handleClick(notif)}
                    className={`w-full text-left px-4 py-3 flex items-start gap-3 hover:bg-virgo-navy-600/50 transition-colors ${
                      !notif.is_read ? 'bg-cyan-500/5 border-l-2 border-cyan-500' : 'border-l-2 border-transparent'
                    }`}
                  >
                    <div className={`p-1.5 rounded-lg flex-shrink-0 ${colorClass}`}>
                      <Icon size={14} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm ${!notif.is_read ? 'text-white font-medium' : 'text-virgo-teal-300'}`}>
                        {notif.title}
                      </p>
                      {notif.body && (
                        <p className="text-xs text-virgo-teal-500 truncate mt-0.5">{notif.body}</p>
                      )}
                      <p className="text-xs text-virgo-teal-600 mt-1">{formatTime(notif.created_at)}</p>
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
